import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { CheckCircle2, XCircle } from "lucide-react";
import { z } from "zod";

import { buttonVariants } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { formatCoachName } from "~/lib/shared/utils/coach-name";

const searchSchema = z.object({
  offerId: z.string().uuid(),
  session_id: z.string().optional(),
  status: z.string().optional(),
});

// Server fn (not a plain call from the loader): the loader runs in the
// browser on client-side navigation, and getDBWithInit must only ever
// execute server-side.
const getSummitThankYouData = createServerFn({ method: "GET" })
  .validator(z.object({ offerId: z.string().uuid() }))
  .handler(async ({ data }) => {
    const { getDBWithInit } = await import("~/lib/db");
    const { OfferType } = await import("~/lib/schemas/db-enums");

    const offer = await (
      await getDBWithInit()
    ).query.offers.findFirst({
      where: {
        id: data.offerId,
      },
      with: { coach: true },
    });

    if (!offer || offer.type !== OfferType.Summit) {
      throw notFound();
    }

    return {
      coachName: formatCoachName(offer.coach),
      offerTitle: offer.headline ?? "Summit Special",
      coachUsername: offer.coach?.username,
    };
  });

export const Route = createFileRoute("/summit-special/thank-you")({
  head: () =>
    createNoIndexMeta(
      "Purchase confirmed",
      "Your summit special purchase is confirmed.",
    ),
  validateSearch: (search) => searchSchema.parse(search ?? {}),
  loaderDeps: ({ search }) => ({ search: searchSchema.parse(search ?? {}) }),
  loader: ({ deps }) =>
    getSummitThankYouData({ data: { offerId: deps.search.offerId } }),
  component: SummitPurchaseThankYouPage,
});

function SummitPurchaseThankYouPage() {
  const { coachName, offerTitle, coachUsername } = Route.useLoaderData();
  const { status } = Route.useSearch();
  const cancelled = status === "cancelled";

  return (
    <div className="flex min-h-dvh items-center justify-center bg-muted/30 p-4">
      <Card className="max-w-lg shadow-lg">
        <CardHeader className="flex items-center gap-3">
          {cancelled ? (
            <XCircle className="h-8 w-8 text-destructive" />
          ) : (
            <CheckCircle2 className="text-affirm h-8 w-8" />
          )}
          <div>
            <CardTitle className="text-xl">
              {cancelled ? "Payment cancelled" : "Payment confirmed"}
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              {cancelled
                ? "Your payment was cancelled. You can start a new checkout from your coach’s link."
                : `You’re all set for ${offerTitle} with ${coachName}.`}
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          {cancelled ? (
            <p>
              No charge was made. If you still want to purchase, return to your
              coach’s link to complete checkout.
            </p>
          ) : (
            <>
              <p>
                We’ve emailed a receipt to you and notified your coach. They’ll
                reach out soon to coordinate next steps.
              </p>
              <p>
                If you need to share details again, keep your confirmation email
                handy—it includes everything from today’s checkout.
              </p>
            </>
          )}
          {coachUsername && (
            <div className="pt-2">
              <a className={buttonVariants({})} href={`/${coachUsername}`}>
                Return to {coachName}’s profile
              </a>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
